/**
 * Per-tier feature limits. Single source of truth used by:
 *
 *   - server-side asserts (`folders.ts`, `themes.ts`, `customDomainRouter`, …)
 *   - client-side gating (`useWorkspacePlanTier` + per-feature paywall modals)
 *   - pricing UI strings (so the cards stay in sync with what the code enforces)
 *
 * Tiers are derived from a workspace's Stripe `price.metadata.title`. Workspaces
 * without `workspace.plan` set fall through to `FREE`.
 */

export type PlanTier = 'FREE' | 'PRO' | 'BUSINESS';

export type PlanLimits = {
  /** Maximum saved form folders (0 hides the feature; use Infinity for unlimited). */
  folders: number;
  /** Maximum saved custom themes (presets stay free for everyone). */
  themes: number;
  /**
   * Maximum verified custom domains. Pro gets a single domain (typical SMB
   * usage: 1 brand, 1 hostname). Business is unlimited so agencies and
   * multi-brand operators can run as many client domains as they like.
   */
  customDomains: number;
  /** Whether the workspace can duplicate forms from the FormActions menu. */
  canDuplicateForms: boolean;
};

export const PLAN_LIMITS: Record<PlanTier, PlanLimits> = {
  FREE: {
    folders: 2,
    themes: 1,
    customDomains: 0,
    canDuplicateForms: false,
  },
  PRO: {
    folders: 5,
    themes: 5,
    customDomains: 1,
    canDuplicateForms: true,
  },
  BUSINESS: {
    folders: Infinity,
    themes: Infinity,
    customDomains: Infinity,
    canDuplicateForms: true,
  },
};

/** Convenience: which tier is required to unlock a given gated feature. */
export const FEATURE_REQUIRED_TIER = {
  customDomains: 'PRO' as const,
  duplicateForm: 'PRO' as const,
  customThemes: 'PRO' as const,
} as const;

/** Display the limit as `"5"` or `"Unlimited"` — used by pricing cards + paywalls. */
export const formatLimitForDisplay = (limit: number): string =>
  Number.isFinite(limit) ? String(limit) : 'Unlimited';

/** `true` when the count has reached the tier's cap (Infinity always returns false). */
export const isAtLimit = (count: number, limit: number): boolean =>
  Number.isFinite(limit) && count >= limit;
